/**
 * Contains methods for manipulating URL addresses.
 */
export class UrlHelper {

    /**
     * Analyzes a specified URL and adds slash to the end if needed. 
     * E.g. https://customerscanvashub.com -> https://customerscanvashub.com/
     * 
     * NOTE: It will fail for relative URLs or non-URL strings.
     * 
     * @param address An absolute URL without path (as a string). E.g. it won't add slash for https://example.com/abc.
     * @returns The same URL with slash in the end (as a string).
     */
    public static ensureTrailingSlash(address: string): string {
        const url = new URL(address);
        return url.toString();
    }

    /**
     * Combines several components of paths to a single URL without worrying about slashes. In other words,
     * it is a URL equivalent to C# `Path.Combine`/NodeJS `path.join`. 
     * 
     * Examples:
     *   - `UrlHelper.urlCombine('https://example.com', 'foo', 'bar/', '/baz/')` -> `https://example.com/foo/bar/baz/` 
     *   - `UrlHelper.urlCombine('https://example.com/foo/', 'bar/', '/baz')` -> `https://example.com/foo/bar/baz` 
     * 
     * The first argument should be an absolute URL. If you need to combine a relative URL, use {@link UrlHelper.relativeUrlCombine}.
     * 
     * @param baseAddress An absolute URL - a beginning of the address.
     * @param paths Any number of path segments to join
     * @returns A URL object containing combined URL.
     */
    public static urlCombine(baseAddress: string, ...paths: string[]): URL {
        const url = new URL(baseAddress);
        const baseAddressPathComponents = url.pathname.split("/");
        url.pathname = this.relativeUrlCombine(...baseAddressPathComponents.concat(paths)); 

        return url;
    }

    /**
     * Same as {@link UrlHelper.urlCombine} but for relative paths. It combines several path segments to 
     * one string and ensures that they are separated with only one slash.
     * 
     * @param paths Any number of path segments to join
     * @returns A string containing a combined relative URL.
     */
    public static relativeUrlCombine(...paths: string[]): string {
        return paths
            .flatMap(x => x.split('/'))
            .filter((x, i, arr) => x != "" || i == 0 || i == arr.length - 1)
            .join("/");
    }

    /**
     * Checks whether a specified string is a http or https absolute path. 
     * 
     * NOTE: It does not check other protocols.
     * 
     * @param address An address to check for being absolute.
     * @returns A boolean value saying if it is absolute or not.
     */
    public static isAbsolute(address: string) {
        return /(^http|^https):\/\/.+/.test(address);
    }

}